import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Video, Mic, Square, RotateCcw, Send, CheckCircle2, Info, Play, AlertCircle, Languages, Loader2, Lock } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import Container from '../common/Container';
import Card from '../common/Card';
import Button from '../common/Button';
import StatusBadge from '../common/StatusBadge';
const API_KEY = process.env.REACT_APP_API_KEY;
function LivenessVideo({ translations, sessionId, userId, onComplete, mode }) {
  const { lang } = useAppContext();
  
  // ✅ خواندن زبان از URL params
  const urlLang = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const langParam = urlParams.get('lang');
    // فقط fa یا en قبول می‌کنیم
    if (langParam === 'fa' || langParam === 'en') {
      return langParam;
    }
    return null;
  }, []);

  // ✅ اگر زبان از URL اومده باشه، قفل می‌شه
  const isLanguageLocked = urlLang !== null;
  
  const [recordingLang, setRecordingLang] = useState(urlLang || 'fa');
  const [status, setStatus] = useState({ type: 'info', message: '' });
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [timer, setTimer] = useState('00:00');
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const videoRef = useRef(null);
  const previewRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerIntervalRef = useRef(null);
  const recordingTimeoutRef = useRef(null);
  const startTimeRef = useRef(0);
  const mimeTypeRef = useRef('video/webm');
  const previewUrlRef = useRef(null);

  const trans = translations?.[lang || 'en'] || translations?.en || {};

  const persianDate = new Date().toLocaleDateString('fa-IR');
  const englishDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const getSentenceText = () => {
    const faText = translations?.['fa']?.sentenceFa || '';
    const enText = translations?.['en']?.sentenceEn || '';
    const sentence = recordingLang === 'fa' ? faText : enText;
    const date = recordingLang === 'fa' ? persianDate : englishDate;
    return sentence.replace('{date}', date);
  };

  const formattedSentence = getSentenceText();

  // Initial camera setup
  useEffect(() => {
    if (!sessionId || !userId) {
      setStatus({ type: 'error', message: lang === 'fa' ? 'جلسه معتبری یافت نشد' : 'No valid session found' });
      return;
    }
    
    const setupCamera = async () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      await initCamera();
    };
    
    setupCamera();
    
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (recordingTimeoutRef.current) clearTimeout(recordingTimeoutRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      }
    };
  }, [sessionId, userId, lang]);

  // Handle preview video loading after element is rendered
  useEffect(() => {
    if (showPreview && previewRef.current && previewUrlRef.current) {
      console.log('[PREVIEW] Loading video into preview element');
      
      const videoElement = previewRef.current;
      videoElement.src = previewUrlRef.current;
      
      const loadTimeout = setTimeout(() => {
        console.warn('[PREVIEW] Video load timeout after 5s');
      }, 5000);
      
      videoElement.onloadedmetadata = () => {
        clearTimeout(loadTimeout);
        console.log('[PREVIEW] Video metadata loaded successfully, duration:', videoElement.duration);
      };
      
      videoElement.onerror = (e) => {
        clearTimeout(loadTimeout);
        console.error('[PREVIEW] Video load error:', e);
        console.log('[PREVIEW] Video src:', videoElement.src);
        console.log('[PREVIEW] Recorded blob size:', recordedBlob?.size);
      };
      
      videoElement.load();
    }
  }, [showPreview, recordedBlob]);

  // Reconnect camera stream after closing preview (retry)
  useEffect(() => {
    if (!showPreview && videoRef.current && streamRef.current) {
      console.log('[CAMERA] Reconnecting stream after preview close');
      
      // Check if stream is still active
      if (streamRef.current.active) {
        videoRef.current.srcObject = streamRef.current;
        videoRef.current.play().catch(err => {
          console.error('[CAMERA] Play error after reconnect:', err);
          // If play fails, reinitialize camera
          initCamera();
        });
      } else {
        // Stream is inactive, reinitialize
        console.log('[CAMERA] Stream inactive, reinitializing...');
        initCamera();
      }
    }
  }, [showPreview]);

  const initCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      console.log('[CAMERA] Requesting camera and microphone access...');
      
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true
      });
      
      console.log('[CAMERA] Access granted, stream active:', stream.active);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
        try {
          await videoRef.current.play();
          console.log('[CAMERA] Video playing');
        } catch (playError) {
          console.error('[CAMERA] Play error:', playError);
        }
      }
      
      setStatus({ type: 'success', message: lang === 'fa' ? 'دوربین آماده است' : 'Camera ready' });
    } catch (err) {
      console.error('[CAMERA] Error:', err);
      
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setStatus({ 
          type: 'error', 
          message: lang === 'fa' 
            ? 'دسترسی به دوربین/میکروفون رد شد. لطفاً در تنظیمات مرورگر خود اجازه دهید.'
            : 'Camera/microphone access denied. Please allow access in your browser settings.'
        });
      } else if (err.name === 'NotFoundError') {
        setStatus({ 
          type: 'error', 
          message: lang === 'fa'
            ? 'دوربین یا میکروفونی یافت نشد. لطفاً دستگاهی متصل کنید.'
            : 'No camera or microphone found. Please connect a device.'
        });
      } else if (err.name === 'NotReadableError') {
        setStatus({ 
          type: 'error', 
          message: lang === 'fa'
            ? 'دوربین در حال استفاده توسط برنامه دیگری است.'
            : 'Camera is already in use by another application.'
        });
      } else {
        setStatus({ 
          type: 'error', 
          message: lang === 'fa'
            ? `خطای دوربین: ${err.message || 'خطای نامشخص'}`
            : `Camera error: ${err.message || 'Unknown error'}`
        });
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const startTimer = () => {
    startTimeRef.current = Date.now();
    timerIntervalRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setTimer(formatTime(elapsed));
    }, 1000);
  };

  const stopTimer = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    setTimer('00:00');
  };

  const startRecording = () => {
    if (!streamRef.current) {
      setStatus({ type: 'error', message: lang === 'fa' ? 'دوربین آماده نیست. لطفاً صفحه را رفرش کنید.' : 'Camera not ready. Please refresh.' });
      return;
    }
    
    if (!streamRef.current.active) {
      setStatus({ type: 'error', message: lang === 'fa' ? 'جریان دوربین غیرفعال است. در حال راه‌اندازی مجدد...' : 'Camera stream inactive. Reinitializing...' });
      initCamera();
      return;
    }
    
    chunksRef.current = [];
    
    // Try different mime types in order of preference
    let mimeType = 'video/webm;codecs=vp9,opus';
    const mimeTypes = [
      'video/webm;codecs=vp9,opus',
      'video/webm;codecs=vp8,opus',
      'video/webm',
      'video/mp4',
      '' // Let browser choose
    ];
    
    for (const type of mimeTypes) {
      if (type === '' || MediaRecorder.isTypeSupported(type)) {
        mimeType = type;
        mimeTypeRef.current = mimeType || 'video/webm';
        console.log('[RECORDING] Using mime type:', mimeTypeRef.current);
        break;
      }
    }
    
    const options = mimeType ? { mimeType } : {};
    
    try {
      const recorder = new MediaRecorder(streamRef.current, options);
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          console.log('[RECORDING] Data chunk received, size:', event.data.size);
          chunksRef.current.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        console.log('[RECORDING] Stop event triggered, chunks:', chunksRef.current.length);
        
        if (chunksRef.current.length === 0) {
          console.error('[RECORDING] No chunks available');
          setStatus({ type: 'error', message: lang === 'fa' ? 'خطا در ضبط ویدیو' : 'Recording error' });
          return;
        }

        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current });
        console.log('[RECORDING] Blob created, size:', blob.size, 'type:', blob.type);
        
        setRecordedBlob(blob);
        
        const url = URL.createObjectURL(blob);
        console.log('[RECORDING] Object URL created:', url);
        
        // Store URL and show preview - useEffect will handle loading
        previewUrlRef.current = url;
        setShowPreview(true);
        setStatus({ type: 'success', message: lang === 'fa' ? 'ضبط کامل شد!' : 'Recording complete!' });
      };
      
      recorder.onerror = (event) => {
        console.error('[RECORDING] Recorder error:', event.error);
        setStatus({ type: 'error', message: lang === 'fa' ? 'خطا در ضبط' : 'Recording error' });
      };
      
      // Request data every second for better reliability
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      startTimer();
      setStatus({ type: 'warning', message: lang === 'fa' ? 'در حال ضبط...' : 'Recording in progress...' });
      
      console.log('[RECORDING] Started with state:', recorder.state);
      
      recordingTimeoutRef.current = setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          console.log('[RECORDING] Auto-stopping after 30 seconds');
          stopRecording();
        }
      }, 30000);
    } catch (err) {
      console.error('[RECORDING] MediaRecorder error:', err);
      setStatus({ type: 'error', message: lang === 'fa' ? `شروع ضبط ناموفق بود: ${err.message}` : `Failed to start recording: ${err.message}` });
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      console.log('[RECORDING] Stopping recorder...');
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      stopTimer();
      
      if (recordingTimeoutRef.current) {
        clearTimeout(recordingTimeoutRef.current);
        recordingTimeoutRef.current = null;
      }
    }
  };

  const retry = () => {
    console.log('[RETRY] Resetting recording...');
    
    // Clean up preview URL
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    
    // Clean up preview element
    if (previewRef.current) {
      previewRef.current.src = '';
      previewRef.current.onloadedmetadata = null;
      previewRef.current.onerror = null;
      previewRef.current.load();
    }
    
    // Reset state - useEffect will handle reconnecting camera
    setShowPreview(false);
    setRecordedBlob(null);
    chunksRef.current = [];
    
    setStatus({ type: 'success', message: lang === 'fa' ? 'آماده ضبط مجدد' : 'Ready to record again' });
  };

  const handleSubmit = async () => {
    if (!recordedBlob) {
      console.warn('[SUBMIT] No blob available');
      return;
    }
    
    setIsSubmitting(true);
    console.log('[SUBMIT] Starting submission, blob size:', recordedBlob.size);
    setStatus({ type: 'info', message: lang === 'fa' ? 'در حال پردازش...' : 'Processing...' });
    
    console.log('[LIVENESS] Submitting with Session ID:', sessionId);
    console.log('[LIVENESS] User ID:', userId);
    console.log('[LIVENESS] Recording language:', recordingLang);

    try {
      const formData = new FormData();
      formData.append('video', recordedBlob, 'liveness_verification.webm');
      formData.append('timestamp', new Date().toISOString());
      formData.append('user_name', '[Full Name]');
      formData.append('recording_language', recordingLang);
      formData.append('session_id', sessionId);

      const response = await fetch('https://clankless-inflatedly-danae.ngrok-free.dev/verify_liveness', {
        method: 'POST',
         headers: {"x-api-key": API_KEY },
        body: formData
      });

      const result = await response.json();
      console.log('[LIVENESS] Response:', result);

      if (result.status === 'SUCCESS') {
        setStatus({ type: 'success', message: lang === 'fa' ? 'تأیید هویت موفق!' : 'Verification successful!' });
        if (onComplete) setTimeout(() => onComplete(result), 1500);
      } else {
        setStatus({ type: 'error', message: `${result.reason || (lang === 'fa' ? 'ناموفق' : 'Failed')}` });
      }
    } catch (err) {
      setStatus({ type: 'error', message: lang === 'fa' ? 'بارگذاری ناموفق بود' : 'Upload failed' });
      console.error('[SUBMIT] Error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ✅ هندل کردن کلیک روی تاگل قفل شده
  const handleToggleClick = () => {
    if (!isLanguageLocked) {
      setRecordingLang(recordingLang === 'fa' ? 'en' : 'fa');
    }
  };

  return (
    <Container>
      {/* Header Section with Step Indicator */}
      <Card variant="elevated" className="mb-6 p-4">
        <div className="flex items-start justify-between mb-6 flex-col">
          <div className="flex-1 mb-4">
            <div className="flex items-center gap-3 mb-4 ">
              <div className="w-10 h-10 rounded-full bg-accent-green/20 flex items-center justify-center">
                <Video className="text-accent-green" size={20} />
              </div>
              <div>
                <h1 className="text-xl font-bold text-text-light">
                  {lang === 'fa' ? 'تأیید زنده بودن ویدیویی' : 'Video Liveness Verification'}
                </h1>
                <p className="text-sm text-text-gray mt-1">
                  {lang === 'fa' ? `مرحله 3 از ${mode === 'automatic' ? '4' : '3'} - فرآیند تایید هویت` : `Step 3 of ${mode === 'automatic' ? '4' : '3'} - Identity Verification Process`}
                </p>
              </div>
            </div>
          </div>
          
          {/* Step Progress Indicator */}
          <div className="flex gap-2">
            <div className="w-12 h-1 bg-accent-green rounded-full"></div>
            <div className="w-12 h-1 bg-accent-green rounded-full"></div>
            <div className="w-12 h-1 bg-accent-green rounded-full"></div>
            {mode === 'automatic' && <div className="w-12 h-1 bg-accent-green/20 rounded-full"></div>}
          </div>
        </div>

        {/* Info Box */}
        <div className="bg-accent-green/10 border-2 border-accent-green/30 rounded-xl p-4 flex gap-3">
          <Info className="text-accent-green flex-shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-text-light">
            <p className="font-semibold mb-1">
              {lang === 'fa' ? 'نحوه عملکرد تأیید ویدیویی:' : 'How video verification works:'}
            </p>
            <ul className="text-text-gray space-y-1 mt-2 list-disc list-inside">
              {lang === 'fa' ? (
                <>
                  <li className='text-xs'>خود را در محیطی با نور کافی قرار دهید</li>
                  <li className='text-xs'>جمله را به وضوح هنگام ضبط بخوانید</li>
                  <li className='text-xs'>اطمینان حاصل کنید چهره شما در تمام مدت قابل مشاهده است</li>
                  <li className='text-xs'>حداکثر زمان ضبط: 30 ثانیه</li>
                </>
              ) : (
                <>
                  <li className='text-xs'>Position yourself in a well-lit area</li>
                  <li className='text-xs'>Read the sentence clearly while recording</li>
                  <li className='text-xs'>Ensure your face is visible throughout</li>
                  <li className='text-xs'>Maximum recording time: 30 seconds</li>
                </>
              )}
            </ul>
          </div>
        </div>

        {sessionId && (
          <div className="mt-4 pt-4 border-t border-accent-green/20">
            <p className="text-xs text-text-gray">
              {lang === 'fa' ? 'شناسه کاربر:' : 'User ID:'} <span className="text-accent-golden font-mono">{userId.slice(0, 16)}...</span>
            </p>
          </div>
        )}
      </Card>

      {/* Language Selector */}
      <Card className="mb-6 p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 w-full">
            <Languages className="text-accent-green" size={45} />
            <div className='w-full'>
              <div className='flex justify-between mb-1'>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-text-light">
                    {lang === 'fa' ? 'زبان ضبط' : 'Recording Language'}
                  </p>
                  {/* ✅ نمایش آیکون قفل اگر زبان از URL اومده */}
                  {isLanguageLocked && (
                    <Lock className="text-accent-golden" size={14} />
                  )}
                </div>
                <div 
                  onClick={handleToggleClick}
                  className={`
                    relative w-[50px] h-[23px] bg-secondary-black rounded-full flex items-center p-1 border-2 border-accent-green/30
                    ${isLanguageLocked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                  `}
                >
                  <div 
                    className={`
                      absolute w-[25px] h-[15px] bg-accent-green rounded-full
                      transition-transform duration-300 ease-out
                      flex items-center justify-center
                      ${lang==="en"?`${recordingLang === 'en' ? 'translate-x-[15px]' : 'translate-x-[-2px]'}`:`${recordingLang === 'en' ? 'translate-x-[-15px]' : 'translate-x-[2px]'}`}
                      
                    `}
                  >
                    <span className="text-[9px] text-white">
                      {recordingLang === 'fa' ? 'FA' : 'EN'}
                    </span>
                  </div>
                </div>
              </div>
              
              {/* ✅ نمایش پیام متفاوت بر اساس قفل بودن زبان */}
              {isLanguageLocked ? (
                <div className="mt-2 p-2 bg-accent-golden/10 border border-accent-golden/30 rounded-lg">
                  <p className="text-xs text-accent-golden">
                    {lang === 'fa' 
                      ? `زبان احراز هویت شما "${recordingLang === 'fa' ? 'فارسی' : 'انگلیسی'}" انتخاب شده است و قابل تغییر نیست.`
                      : `Your verification language is set to "${recordingLang === 'fa' ? 'Persian' : 'English'}" and cannot be changed.`
                    }
                  </p>
                </div>
              ) : (
                <p className="text-xs text-text-gray mt-0.5">
                  {lang === 'fa' ? 'زبان مورد نظر خود را برای جمله تأیید انتخاب کنید' : 'Choose your preferred language for the verification sentence'}
                </p>
              )}
            </div>
          </div>
        </div>
      </Card>

      {/* Status Display */}
      {status.message && (
        <div className="mb-6">
          <StatusBadge type={status.type} showIcon={true}>
            {status.message}
          </StatusBadge>
          {status.type === 'error' && (
            <div className="mt-3">
              <Button onClick={initCamera} variant="outline" size="sm" className='font-normal' fullWidth>
                <AlertCircle className="inline mx-2" size={16} />
                {lang === 'fa' ? 'تلاش مجدد برای دسترسی به دوربین' : 'Retry Camera Access'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Video Preview / Recording */}
      {!showPreview && (
        <Card variant="elevated" className="mb-6">
          <div className="relative bg-primary-black rounded-2xl overflow-hidden">
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted 
              className="w-full h-[400px] block object-cover"
              style={{ transform: 'scaleX(-1)' }}
            />
            
            {/* Recording Indicator */}
            {isRecording && (
              <>
                <div className="absolute top-4 right-4 flex items-center gap-2 bg-red-500/90 px-3 py-2 rounded-full">
                  <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                  <span className="text-white text-sm font-semibold">REC</span>
                </div>
                
                <div className="absolute top-4 left-4 bg-black/70 px-4 py-2 rounded-full">
                  <span className="text-white text-base font-mono font-bold">{timer}</span>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
      
      {/* Sentence to Read */}
      <Card className="mb-6 p-6 border-2 border-accent-green/30">
        <div className="flex items-center gap-2 mb-3">
          <Mic className="text-accent-green" size={18} />
          <h3 className="text-sm font-semibold text-accent-green uppercase tracking-wider">
            {lang === 'fa' ? 'لطفاً این جمله را بخوانید' : 'Please Read This Sentence'}
          </h3>
        </div>
        <div 
          className="text-center text-lg leading-relaxed min-h-[100px] flex items-center justify-center"
          style={{ direction: recordingLang === 'fa' ? 'rtl' : 'ltr' }}
        >
          <p className="text-text-light text-sm text-start">
            {formattedSentence.split(recordingLang === 'fa' ? persianDate : englishDate).map((part, i, arr) => (
              <React.Fragment key={i}>
                {part}
                {i < arr.length - 1 && (
                  <span className="text-accent-green font-bold">
                    {recordingLang === 'fa' ? persianDate : englishDate}
                  </span>
                )}
              </React.Fragment>
            ))}
          </p>
        </div>
      </Card>

      {/* Action Buttons - Recording */}
      {!showPreview && (
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Button 
            onClick={startRecording} 
            disabled={isRecording}
            variant="primary"
            size="sm"
            fullWidth
          >
            <Play className="inline mx-2" size={18} />
            {lang === 'fa' ? 'شروع ضبط' : 'Start Recording'}
          </Button>
          
          <Button 
            onClick={stopRecording} 
            disabled={!isRecording}
            variant="secondary"
            size="sm"
            fullWidth
          >
            <Square className="inline mx-2" size={18} />
            {lang === 'fa' ? 'توقف ضبط' : 'Stop Recording'}
          </Button>
        </div>
      )}

      {/* Preview Section */}
      {showPreview && (
        <>
          <Card className="mb-6 p-6">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="text-accent-green" size={20} />
              <h3 className="font-semibold text-text-light">
                {lang === 'fa' ? 'پیش‌نمایش ضبط' : 'Recording Preview'}
              </h3>
            </div>
            
            <div className="bg-accent-green/10 border-2 border-accent-green/30 rounded-xl p-3 mb-4 text-center">
              <p className="text-sm text-text-light">
                {lang === 'fa' ? 'ضبط شده به زبان:' : 'Recorded in:'} {' '}
                <span className="font-bold text-accent-green">
                  {recordingLang === 'fa' ? 'فارسی (Persian)' : 'English'}
                </span>
              </p>
            </div>
            
            <div className="bg-primary-black rounded-xl overflow-hidden">
              <video 
                ref={previewRef} 
                controls 
                className="w-full h-[400px] block object-cover"
                preload="auto"
              />
            </div>
          </Card>

          {/* Action Buttons - Preview */}
          <div className="grid grid-cols-2 gap-3">
            <Button 
              onClick={retry} 
              variant="outline" 
              size="sm" 
              fullWidth
              disabled={isSubmitting}
            >
              <RotateCcw className="inline mx-2" size={18} />
              {lang === 'fa' ? 'تلاش مجدد' : 'Retry'}
            </Button>
            
            <Button 
              onClick={handleSubmit} 
              variant="primary" 
              size="sm" 
              fullWidth
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="inline mx-2 animate-spin" size={18} />
                  {lang === 'fa' ? 'در حال ارسال...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <Send className="inline mx-2" size={18} />
                  {lang === 'fa' ? 'ارسال' : 'Submit'}
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </Container>
  );
}

export default LivenessVideo;